import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useApp, ROLE_LABELS } from '../../context/AppContext';
import { toast } from 'sonner';
import {
  Users, MapPin, Store, Calendar,
  BarChart3, ChevronLeft, ChevronRight, GitBranch, Eye,
  ShieldCheck, Download, Shield, X, LogOut, Navigation, FileCode2, UserPlus, Receipt, IndianRupee, Activity, Package, Bell, CreditCard, FileSpreadsheet, Route, Play, ChevronDown, PieChart, Wallet, Phone, ClipboardList, PhoneCall, Landmark, FileText, AlarmClock, Package2, Scale, Fingerprint, Megaphone, Mail as MailIcon, ShieldAlert, BookmarkCheck, Layers, PackagePlus, Trash2, ListChecks, KeyRound, LifeBuoy, Bot, ScanSearch, Calculator
} from 'lucide-react';

// ─── Navigation structure ────────────────────────────────────────────────────
// Grouped: Common → Field Activity (with sub-items) → FASTag → Operations → Admin
// Within each group, items are alphabetical by label.
// Items with `roles: [...]` are only shown to users whose role is in that list.

export const navGroups = [
  {
    key: 'common',
    label: null, // no group header
    items: [
      { to: '/org-tree', icon: GitBranch, label: 'Org Tree', featureKey: 'org-tree' },
      { to: '/team', icon: Users, label: 'Team Management', featureKey: 'team' },
      { to: '/mobile-lookup', icon: PhoneCall, label: 'Mobile Lookup', featureKey: 'mobile-lookup' },
      { to: '/third-party-logins', icon: KeyRound, label: 'Third-Party Logins', featureKey: 'third-party-logins' },
    ],
  },
  {
    key: 'field',
    label: 'Field Activity',
    icon: Navigation,
    featureKey: 'visits',
    collapsible: true,
    items: [
      { to: '/field-activity', icon: Navigation, label: 'Overview', featureKey: 'field-activity' },
      { to: '/travel-plans', icon: Route, label: 'Travel Plans', featureKey: 'travel-plans' },
      { to: '/unplanned-visits', icon: Play, label: 'Unplanned Visits', featureKey: 'unplanned-visits' },
      { to: '/visits', icon: Calendar, label: 'Visits', featureKey: 'visits' },
      { to: '/recently-deleted', icon: Trash2, label: 'Recently Deleted',
        roles: ['admin', 'sub_admin', 'business_head', 'zonal_head', 'sales_manager'] },
    ],
  },
  {
    key: 'fastag',
    label: 'FASTag',
    businessLineKey: 'fastag',
    items: [
      { to: '/revenue', icon: IndianRupee, label: 'FASTag Onboarding', featureKey: 'revenue', cat: 'fastag' },
      { to: '/margin', icon: PieChart, label: 'Margin Management', featureKey: 'margin' },
      { to: '/inventory', icon: Package, label: 'Inventory', featureKey: 'inventory' },
      { to: '/idfc-toll-txn', icon: Landmark, label: 'IDFC Toll Txns', featureKey: 'idfc-toll',
        roles: ['admin', 'sub_admin', 'accounts_head', 'accounts_manager'] },
      { to: '/livquik-toll-txn', icon: Landmark, label: 'LivQuik Toll Txns', featureKey: 'livquik-toll',
        roles: ['admin', 'sub_admin', 'accounts_head', 'accounts_manager'] },
      { to: '/performance', icon: BarChart3, label: 'Performance', featureKey: 'performance', cat: 'fastag' },
      { to: '/perf-watch', icon: Eye, label: 'Watchlist Performance', featureKey: 'perf-watch' },
      { to: '/vrn-tracer', icon: FileSpreadsheet, label: 'VRN Tracer', featureKey: 'vrn-tracer' },
      { to: '/payout', icon: Wallet, label: 'FASTag Payout', featureKey: 'payout' },
    ],
  },
  {
    key: 'hitchzone',
    label: 'Hitch Zone',
    businessLineKey: 'banking',
    items: [
      { to: '/banking', icon: CreditCard, label: 'Hitch Zone Txns', featureKey: 'banking' },
      { to: '/tally', icon: Calculator, label: 'Tally Ledgers', featureKey: 'tally',
        roles: ['admin', 'sub_admin', 'business_head'] },
    ],
  },
  {
    key: 'prepaid_cards',
    label: 'Prepaid Cards',
    businessLineKey: 'prepaid_cards',
    items: [
      { to: '/revenue', icon: IndianRupee, label: 'Prepaid Card Onboarding', featureKey: 'prepaid-onboarding', cat: 'prepaid' },
      { to: '/performance', icon: BarChart3, label: 'Prepaid Card Performance', featureKey: 'prepaid-performance', cat: 'prepaid' },
      { to: '/prepaid-card-dispatch', icon: Package2, label: 'Dispatch Report', featureKey: 'prepaid-card' },
      { to: '/ncmc-inventory', icon: Package, label: 'Prepaid Card Inventory', featureKey: 'ncmc-inventory' },
    ],
  },
  {
    key: 'operations',
    label: 'Operations',
    items: [
      { to: '/support-chat', icon: LifeBuoy, label: 'Support Chat', featureKey: 'support-chat' },
      { to: '/approvals', icon: ShieldCheck, label: 'Approvals', featureKey: 'approvals' },
      { to: '/attendance', icon: Fingerprint, label: 'Attendance', featureKey: 'attendance' },
      { to: '/places', icon: MapPin, label: 'Business Places', featureKey: 'places' },
      { to: '/expense-claims', icon: Receipt, label: 'Expense Claims', featureKey: 'expense-claims' },
      { to: '/leads', icon: UserPlus, label: 'Leads', featureKey: 'leads' },
      { to: '/lead-distribution', icon: Users, label: 'Lead Distribution', featureKey: 'lead-distribution' },
      { to: '/lead-tracking', icon: ListChecks, label: 'Lead Tracking', featureKey: 'lead-tracking' },
      { to: '/call-tracker', icon: Phone, label: 'Call Tracker', featureKey: 'call-tracker' },
      { to: '/task-tracker', icon: ClipboardList, label: 'Task Tracker', featureKey: 'task-tracker' },
      { to: '/notes', icon: FileText, label: 'My Notes', featureKey: 'notes' },
      { to: '/sheet-import', icon: FileSpreadsheet, label: 'Google Sheet', featureKey: 'sheet-import' },
      { to: '/reminders', icon: AlarmClock, label: 'Reminders', featureKey: 'reminders' },
      { to: '/hitchzone-audit', icon: ShieldAlert, label: 'Txn Highlighter', featureKey: 'txn-highlighter',
        roles: ['admin', 'sub_admin', 'business_head'] },
      { to: '/sales-points', icon: Store, label: 'Sales Points', featureKey: 'sales-points' },
      { to: '/txn-auditor', icon: Scale, label: 'Txn Auditor', featureKey: 'txn-audit',
        roles: ['admin', 'sub_admin', 'accounts_head', 'accounts_manager'] },
      { to: '/hiteam-wallet-txns', icon: Wallet, label: 'HiTeam Wallet Txn', featureKey: 'txn-audit',
        roles: ['admin', 'sub_admin', 'accounts_head', 'accounts_manager'] },
      { to: '/wallet-audit', icon: ScanSearch, label: 'HiTeam Wallet Audit', featureKey: 'txn-audit',
        roles: ['admin', 'sub_admin', 'accounts_head', 'accounts_manager'] },
    ],
  },
  {
    key: 'marketing',
    label: 'Marketing',
    items: [
      { to: '/notifications', icon: Megaphone, label: 'hitch.services Notification', featureKey: 'hitch-notifications',
        roles: ['admin', 'sub_admin', 'business_head'] },
      { to: '/push-notifications', icon: Bell, label: 'HiTeam Push Notifications', featureKey: 'push-notifications' },
      { to: '/email-notifications', icon: MailIcon, label: 'Email Notification', featureKey: 'email-notifications',
        roles: ['admin', 'sub_admin', 'business_head'] },
    ],
  },
  {
    key: 'automobile',
    label: 'Automobile',
    items: [
      { to: '/vehicle-inventory', icon: Package2, label: 'Vehicle Inventory', featureKey: 'vehicle-inventory',
        roles: ['admin', 'sub_admin', 'business_head', 'accounts_head', 'accounts_manager'] },
      { to: '/vehicle-addition', icon: PackagePlus, label: 'Vehicle Addition', featureKey: 'vehicle-inventory',
        roles: ['admin', 'sub_admin', 'business_head', 'accounts_head', 'accounts_manager'] },
      { to: '/vehicle-bookings', icon: BookmarkCheck, label: 'Bookings', featureKey: 'vehicle-bookings',
        roles: ['admin', 'sub_admin', 'business_head', 'accounts_head', 'accounts_manager'] },
      { to: '/payments', icon: CreditCard, label: 'Customer Payments', featureKey: 'vehicle-bookings',
        roles: ['admin', 'sub_admin', 'business_head', 'accounts_head', 'accounts_manager'] },
      { to: '/vehicle-model-master', icon: Layers, label: 'Model Master', featureKey: 'vehicle-model-master',
        roles: ['admin', 'sub_admin', 'business_head', 'accounts_head', 'accounts_manager'] },
      { to: '/vehicle-pricing', icon: IndianRupee, label: 'Pricing', featureKey: 'vehicle-pricing',
        roles: ['admin', 'sub_admin', 'accounts_head', 'accounts_manager', 'business_head', 'zonal_head', 'sales_manager', 'sales_coordinator', 'team_leader', 'sales'] },
      { to: '/create-quote', icon: Receipt, label: 'Create Quote', featureKey: 'vehicle-pricing',
        roles: ['admin', 'sub_admin', 'accounts_head', 'accounts_manager', 'business_head', 'zonal_head', 'sales_manager', 'sales_coordinator', 'team_leader', 'sales'] },
      { to: '/billing', icon: FileText, label: 'Billing (Preview)', featureKey: 'vehicle-pricing',
        roles: ['admin', 'sub_admin', 'accounts_head', 'accounts_manager', 'business_head', 'zonal_head', 'sales_manager', 'sales_coordinator', 'team_leader', 'sales'] },
    ],
  },
  {
    key: 'hr',
    label: 'HR',
    items: [
      { to: '/hr-dashboard', icon: BarChart3, label: 'HR Dashboard', featureKey: 'hr-dashboard' },
    ],
  },
  {
    key: 'admin',
    label: 'Admin',
    items: [
      { to: '/admin', icon: Shield, label: 'Admin Panel', featureKey: 'admin' },
      { to: '/chatbot-admin', icon: Bot, label: 'ChatBot Hub', featureKey: 'chatbot-admin', roles: ['admin', 'hr'] },
      { to: '/onboard-id-list', icon: ListChecks, label: 'Onboard ID List', featureKey: 'onboard-id-list',
        roles: ['admin', 'sub_admin', 'business_head'] },
      { to: '/sub-users', icon: Users, label: 'Sub Users', featureKey: 'admin' },
      { to: '/docs', icon: FileCode2, label: 'API Docs', featureKey: 'admin' },
      { to: '/import', icon: Download, label: 'Data Import', featureKey: 'import' },
      { to: '/diagnostics', icon: Activity, label: 'System Test', featureKey: 'system-test' },
    ],
  },
];

// Live feature catalog derived from the sidebar nav — single source of truth for
// the Department Access, Team Management and Sub-User feature-mapping screens.
// Returns [{ group, businessLineKey, features: [{ key, label }] }].
// Derive the gating key for a nav item. Falls back to the route so EVERY menu
// item is gateable and auto-appears in Business Line Access — even if a dev
// forgets to set an explicit featureKey on a new feature.
export const featureKeyForItem = (it) =>
  it.featureKey || (it.to === '/sheet-import' ? 'sheet-import' : (it.to ? it.to.replace(/^\//, '') : null));

// SINGLE SOURCE for feature lists. One entry per gating key (so grant screens
// show one control per key), but each entry carries `labels[]` with every page
// name that shares the key. Adding any item to navGroups auto-registers it here
// — even without an explicit featureKey (we derive one from the route).
export const buildFeatureCatalog = () => navGroups
  .map(g => {
    const order = [];
    const byKey = {};
    (g.items || []).forEach(it => {
      const key = featureKeyForItem(it);
      if (!key) return;
      if (!byKey[key]) { byKey[key] = { key, label: it.label, labels: [it.label] }; order.push(key); }
      else if (!byKey[key].labels.includes(it.label)) byKey[key].labels.push(it.label);
    });
    return { group: g.label || 'Common', businessLineKey: g.businessLineKey || null, features: order.map(k => byKey[k]) };
  })
  .filter(g => g.features.length > 0);

// Keys that previously shared featureKey 'admin' — sub-users already granted
// 'admin' keep seeing these tools (backward compatibility).
export const LEGACY_ADMIN_KEYS = ['hitch-notifications', 'email-notifications', 'txn-highlighter'];

// Split features keep backward-compatible grant aliases: anyone who was granted
// the OLD shared key still keeps access to the split-out page (no data migration,
// no lockouts). Aliases apply to GRANT checks only — business-line gating uses the
// new key so each page can be controlled independently.
export const LEGACY_FEATURE_ALIASES = {
  'ncmc-inventory': ['prepaid-card'],
  'prepaid-performance': ['performance'],
};

// key → label map across every group (for summary chips).
export const featureLabelMap = () => buildFeatureCatalog().reduce((acc, g) => {
  g.features.forEach(f => { acc[f.key] = f.label; });
  return acc;
}, {});

// Business-line gating (admin-configurable). A feature linked to no business line
// is COMMON (visible to all). A SPECIFIC feature shows only if the user's assigned
// business line matches; a user with NO business line sees only COMMON features.
export const isFeatureVisibleForBiz = (featureKey, assignedBusinesses, blFeatureMap) => {
  const linked = (blFeatureMap && blFeatureMap[featureKey]) || [];
  if (linked.length === 0) return true;                 // COMMON / irrespective
  const userBiz = assignedBusinesses || [];
  if (userBiz.length === 0) return false;               // no line → only COMMON
  return linked.some(id => userBiz.includes(id));
};

// Sales-Department GRANT (union): a function LINKED to the Sales Department is
// granted to any user whose DEPARTMENT is a configured Sales department — in
// ADDITION to their Department Access. Business Line Access is applied afterwards.
export const isFeatureGrantedBySalesDept = (featureKey, department, salesDeptConfig) => {
  const fns = (salesDeptConfig && salesDeptConfig.linked_functions) || [];
  if (!fns.includes(featureKey)) return false;   // not a sales-linked function
  const slug = (department || '').trim().toLowerCase().replace(/\s+/g, '_').replace(/\//g, '_');
  return ((salesDeptConfig && salesDeptConfig.departments) || []).includes(slug);
};

const _featureKeyOf = (item) => featureKeyForItem(item);

// ── SINGLE SOURCE OF TRUTH for nav-item visibility ──────────────────────────
// Pure function used by BOTH the desktop Sidebar and the mobile button launcher
// so access rules (role + department + business-line gating) never drift apart.
export const isNavItemVisible = (item, ctx) => {
  const { currentUser, roleFeatures = [], departmentFeatures = [], blFeatureMap, salesDeptConfig } = ctx || {};
  const role = (currentUser?.role || '').toLowerCase();
  const isPrivileged = ['admin', 'sub_admin', 'business_head'].includes(role);
  const hasDept = !!(currentUser?.department);
  const isDeptGated = !currentUser?.is_sub_user && !isPrivileged;

  if (currentUser?.role === 'hr' && item.to === '/admin') return true;
  if (item.to === '/third-party-logins') return true;
  if (item.to === '/support-chat') return true;
  if (item.to === '/chatbot-admin') return ['admin', 'hr'].includes(currentUser?.role);

  if (currentUser?.is_sub_user) {
    const fk = item.featureKey;
    const aliases = LEGACY_FEATURE_ALIASES[fk] || [];
    return roleFeatures.includes(fk) ||
      aliases.some(k => roleFeatures.includes(k)) ||
      (LEGACY_ADMIN_KEYS.includes(fk) && roleFeatures.includes('admin'));
  }

  if (isDeptGated) {
    if (item.to === '/expense-claims' && ['accounts_head', 'accounts_manager'].includes(currentUser?.role)) return true;
    if ((item.to === '/vehicle-pricing' || item.to === '/create-quote' || item.to === '/billing') && ['accounts_head', 'accounts_manager', 'business_head', 'zonal_head', 'sales_manager', 'sales_coordinator', 'team_leader', 'sales'].includes(currentUser?.role)) return true;
    const fk = _featureKeyOf(item);
    if (!fk) return false;
    // Grant check accepts the new key OR any legacy alias (so old grants still work).
    const grantKeys = [fk, ...(LEGACY_FEATURE_ALIASES[fk] || [])];
    if (fk === 'vehicle-pricing') grantKeys.push('vehicle-inventory');
    const grantedByDept = hasDept && grantKeys.some(k => departmentFeatures.includes(k));
    const grantedBySales = grantKeys.some(k => isFeatureGrantedBySalesDept(k, currentUser?.department, salesDeptConfig));
    if (!grantedByDept && !grantedBySales) return false;
    // Business-line gating uses the new key only, so each page is independently controllable.
    const blKeys = fk === 'vehicle-pricing' ? ['vehicle-pricing', 'vehicle-inventory'] : [fk];
    return blKeys.some(k => isFeatureVisibleForBiz(k, currentUser?.assigned_businesses, blFeatureMap));
  }

  if (item.roles) return item.roles.includes(currentUser?.role);
  if (item.to === '/admin') return ['admin', 'business_head', 'accounts_head', 'sub_admin'].includes(currentUser?.role);
  if (item.to === '/sub-users') return ['admin', 'sub_admin', 'business_head'].includes(currentUser?.role);
  return true;
};

// Hook → returns the nav groups filtered to what the current user can see,
// flattening any sub-items so launcher tiles map 1:1 to real routes.
export const useVisibleNav = () => {
  const { currentUser, roleFeatures, departmentFeatures, blFeatureMap, salesDeptConfig } = useApp();
  const ctx = { currentUser, roleFeatures, departmentFeatures, blFeatureMap, salesDeptConfig };
  return navGroups
    .map(g => {
      const items = [];
      const seen = new Set();
      (g.items || []).forEach(it => {
        const list = it.children ? it.children : [it];
        list.forEach(child => {
          if (child.to && !seen.has(child.to) && isNavItemVisible(child, ctx)) {
            seen.add(child.to);
            items.push(child);
          }
        });
      });
      return { key: g.key, label: g.label || 'General', icon: g.icon, items };
    })
    .filter(g => g.items.length > 0);
};


export const Sidebar = ({ collapsed, onToggle, isMobile, mobileOpen, onMobileClose, onProfileClick }) => {
  const { currentUser, setCurrentUser, roleFeatures, departmentFeatures, setSelectedBusiness, businessLines, blFeatureMap, salesDeptConfig } = useApp();
  const location = useLocation();
  const [expandedGroups, setExpandedGroups] = useState({ field: true });
  // PWA install prompt handling
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  useEffect(() => {
    const onBeforeInstall = (e) => { e.preventDefault(); setInstallPrompt(e); };
    const onInstalled = () => { setIsInstalled(true); setInstallPrompt(null); };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) setIsInstalled(true);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);
  const handleInstallApp = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      try { await installPrompt.userChoice; } catch (e) { /* ignore */ }
      setInstallPrompt(null);
    } else {
      const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
      toast.info(isIOS
        ? 'On iPhone/iPad: tap the Share button, then "Add to Home Screen".'
        : 'Open the browser menu and choose "Install app" / "Add to Home Screen".');
    }
  };

  const [pushOn, setPushOn] = useState(typeof Notification !== 'undefined' && Notification.permission === 'granted');
  const pushSupported = typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
  const handleEnablePush = async () => {
    try {
      const { enablePush } = await import('../../lib/webPush');
      const ok = await enablePush(currentUser?.id, { prompt: true });
      if (ok) { setPushOn(true); toast.success("Alerts enabled — you'll get lead notifications on this device."); }
      else if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
        toast.error('Notifications are blocked. Allow them for this site in your browser settings, then try again.');
      } else {
        toast.error('Could not enable alerts. Please try again.');
      }
    } catch (e) { toast.error('Could not enable alerts.'); }
  };

  const toggleGroup = (key) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const role = (currentUser?.role || '').toLowerCase();
  // Privileged roles bypass department gating and see everything (existing behavior).
  const isPrivileged = ['admin', 'sub_admin', 'business_head'].includes(role);
  const hasDept = !!(currentUser?.department);
  const deptFeatures = departmentFeatures || [];
  // Regular (non-privileged, non-sub-user) users are DEPARTMENT-gated.
  const isDeptGated = !currentUser?.is_sub_user && !isPrivileged;

  // A group is visible if at least one of its items is visible. Item visibility
  // now encodes department + business-line gating, so no separate BL logic here.
  const isGroupVisible = (group) => (group.items || []).some(it => isItemVisible(it));

  const _featureKeyOf = (item) => featureKeyForItem(item);

  const isItemVisible = (item) => isNavItemVisible(item, {
    currentUser, roleFeatures, departmentFeatures: deptFeatures, blFeatureMap, salesDeptConfig,
  });

  const handleNavClick = () => {
    if (isMobile && onMobileClose) onMobileClose();
  };

  const renderNavItem = (item) => {
    // Onboarding category items (FASTag / Prepaid Card) share the /revenue page.
    // Clicking sets the active category and (re)loads so every panel stays in sync.
    if (item.cat) {
      const base = item.to;
      const active = location.pathname.startsWith(base) &&
        (localStorage.getItem('revenue_category') || 'fastag') === item.cat;
      const onClick = () => {
        if (isMobile && onMobileClose) onMobileClose();
        const cur = localStorage.getItem('revenue_category') || 'fastag';
        localStorage.setItem('revenue_category', item.cat);
        if (location.pathname.startsWith(base)) {
          if (cur !== item.cat) window.location.reload();
        } else {
          window.location.assign(base);
        }
      };
      return (
        <button
          key={`${item.to}-${item.cat}`}
          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}-${item.cat}`}
          onClick={onClick}
          title={collapsed && !isMobile ? item.label : undefined}
          className={`sidebar-link w-full text-left ${active ? 'active' : ''}`}
        >
          <item.icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
          {(!collapsed || isMobile) && <span>{item.label}</span>}
        </button>
      );
    }
    const isActive = location.pathname === item.to ||
      (item.to !== '/' && location.pathname.startsWith(item.to));
    return (
      <NavLink
        key={item.to}
        to={item.to}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        className={`sidebar-link ${isActive ? 'active' : ''}`}
        title={collapsed && !isMobile ? item.label : undefined}
        onClick={handleNavClick}
      >
        <item.icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
        {(!collapsed || isMobile) && <span>{item.label}</span>}
      </NavLink>
    );
  };

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="p-6 border-b border-border/40 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.jpg" alt="CRM" className="w-9 h-9 rounded-lg flex-shrink-0 object-cover" />
          {(!collapsed || isMobile) && (
            <div className="overflow-hidden">
              <h1 className="text-base font-bold text-slate-900 truncate">CRM</h1>
              <p className="text-xs text-slate-400 truncate">Performance Hub</p>
            </div>
          )}
        </div>
        {isMobile && (
          <button onClick={onMobileClose} className="p-1.5 rounded-lg hover:bg-slate-100">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        )}
      </div>

      {/* Nav Links — grouped */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navGroups.filter(isGroupVisible).map(group => {
          const visibleItems = group.items.filter(isItemVisible);
          if (visibleItems.length === 0) return null;

          // For collapsible groups (Field Activity)
          if (group.collapsible) {
            const isExpanded = expandedGroups[group.key];
            const anyActive = visibleItems.some(item =>
              location.pathname === item.to || (item.to !== '/' && location.pathname.startsWith(item.to))
            );
            return (
              <div key={group.key}>
                {group.label && (!collapsed || isMobile) && (
                  <button
                    onClick={() => toggleGroup(group.key)}
                    className={`w-full sidebar-link flex items-center justify-between ${anyActive ? 'active' : ''}`}
                  >
                    <span className="flex items-center gap-2">
                      {group.icon && <group.icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />}
                      <span>{group.label}</span>
                    </span>
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
                  </button>
                )}
                {(isExpanded || collapsed) && (
                  <div className={`${(!collapsed || isMobile) ? 'ml-4 border-l border-slate-200 pl-2' : ''} space-y-0.5`}>
                    {visibleItems.map(renderNavItem)}
                  </div>
                )}
              </div>
            );
          }

          // Regular groups
          return (
            <div key={group.key}>
              {group.label && (!collapsed || isMobile) && (
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mt-4 mb-1 px-3">
                  {group.label}
                </p>
              )}
              {visibleItems.map(renderNavItem)}
            </div>
          );
        })}
        {isDeptGated && (!hasDept || deptFeatures.length === 0) && (!collapsed || isMobile) && (
          <div className="mt-4 mx-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-800" data-testid="no-department-notice">
            No sections assigned yet. Please ask your administrator to set your <b>department</b> to unlock your features.
          </div>
        )}
      </nav>

      {/* Install as App (PWA) */}
      {!isInstalled && (
        <div className="px-2 pb-1">
          <button
            data-testid="install-app-btn"
            onClick={handleInstallApp}
            title="Install App"
            className={`w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors ${collapsed && !isMobile ? 'justify-center' : ''}`}
          >
            <Download className="w-4 h-4 flex-shrink-0" />
            {(!collapsed || isMobile) && <span>Install App</span>}
          </button>
        </div>
      )}

      {/* Enable browser/device push alerts (needs a user gesture to prompt) */}
      {currentUser && pushSupported && !pushOn && (
        <div className="px-2 pb-1">
          <button
            data-testid="enable-push-btn"
            onClick={handleEnablePush}
            title="Enable notifications"
            className={`w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition-colors ${collapsed && !isMobile ? 'justify-center' : ''}`}
          >
            <Bell className="w-4 h-4 flex-shrink-0" />
            {(!collapsed || isMobile) && <span>Enable Alerts</span>}
          </button>
        </div>
      )}

      {/* User info */}
      {currentUser && (
        <div className="p-4 border-t border-border/40">
          <div className={`flex items-center gap-3 ${collapsed && !isMobile ? 'justify-center' : ''}`}>
            <button
              data-testid="profile-avatar-btn"
              onClick={onProfileClick}
              title="My Profile"
              className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-sm flex-shrink-0 hover:ring-2 hover:ring-amber-300 transition-all cursor-pointer"
            >
              {currentUser.name?.charAt(0) || 'U'}
            </button>
            {(!collapsed || isMobile) && (
              <button onClick={onProfileClick} className="overflow-hidden flex-1 text-left hover:opacity-80 transition-opacity cursor-pointer">
                <p className="text-sm font-semibold text-slate-900 truncate">{currentUser.name}</p>
                <p className="text-xs text-slate-400 truncate">{ROLE_LABELS[currentUser.role] || currentUser.role}</p>
              </button>
            )}
            <button
              data-testid="logout-btn"
              onClick={() => { setCurrentUser(null); setSelectedBusiness(null); }}
              title="Logout"
              className={`flex-shrink-0 p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors ${collapsed && !isMobile ? 'mt-2' : ''}`}
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );

  // Mobile overlay sidebar
  if (isMobile) {
    return (
      <>
        {mobileOpen && (
          <div data-testid="mobile-backdrop" className="fixed inset-0 bg-black/40 z-40 transition-opacity" onClick={onMobileClose} />
        )}
        <aside
          data-testid="sidebar"
          className={`fixed left-0 top-0 h-full w-72 bg-white z-50 flex flex-col shadow-xl transition-transform duration-300 ${
            mobileOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          {sidebarContent}
        </aside>
      </>
    );
  }

  // Desktop sidebar
  return (
    <aside
      data-testid="sidebar"
      className={`fixed left-0 top-0 h-full border-r border-border bg-white z-50 transition-all duration-300 ${collapsed ? 'w-20' : 'w-64'} flex flex-col`}
    >
      {sidebarContent}
      <button
        data-testid="sidebar-toggle"
        onClick={onToggle}
        className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-white border border-border shadow-sm flex items-center justify-center hover:bg-slate-50 transition-colors"
      >
        {collapsed ? <ChevronRight className="w-3.5 h-3.5 text-slate-500" /> : <ChevronLeft className="w-3.5 h-3.5 text-slate-500" />}
      </button>
    </aside>
  );
};
